<template>
  <ButtonTemplateMicInput :on-click="handleSpeaker" :text-button="textButton" :is-data-off="!isSoundOn">
    <template #icon-handler>
      <component :is="isSoundOn ? SoundIcon : SoundOffIcon" />
    </template>
  </ButtonTemplateMicInput>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { storeToRefs } from 'pinia';

import ButtonTemplateMicInput from '@/components/domains/chat/common/ButtonTemplateMicInput.vue';
import SoundIcon from '@/components/icons/IconSound.vue';
import SoundOffIcon from '@/components/icons/IconSoundOff.vue';
import { useAudioStore } from '@/stores/audio';
import { useMicrophoneStore } from '@/stores/microphone';
import { useSystemAudioMicrophoneStore } from '@/stores/systemAudioMicrophone';
import { useToastStore } from '@/stores/toast';
import { TypeCall } from '@/types/common';

const { t } = useI18n();
const audioStore = useAudioStore();
const { isSoundOn, languageOptions, languageSelected } = storeToRefs(audioStore);
const { toggleSound } = audioStore;
const microphoneStore = useMicrophoneStore();
const systemAudioMicrophoneStore = useSystemAudioMicrophoneStore();
const toastStore = useToastStore();

const props = withDefaults(
  defineProps<{
    typeCall: TypeCall;
  }>(),
  {
    typeCall: 'GENERAL',
  }
);

const textButton = computed<string>(() => {
  return isSoundOn.value ? t('listening') : t('sound_off');
});

const isMeetingMicOn = computed<boolean>(() => {
  return props.typeCall === 'MEETING' && (microphoneStore.isMicOn || systemAudioMicrophoneStore.isMicOn);
});

const getErrorMessage = (): string => {
  if (isMeetingMicOn.value) {
    return t('sound_unavailable_mic_on');
  }

  if (languageSelected.value && !languageOptions.value[languageSelected.value.code].voiceOutputEnabled) {
    return t('not_support_audio_play');
  }

  return '';
};

const handleSpeaker = () => {
  const message = getErrorMessage();
  if (message) {
    toastStore.warn(message);
    return;
  }

  toggleSound();

  if (isSoundOn.value) window.dispatchEvent(new Event('unlock-audio'));
};

onUnmounted(() => {
  isSoundOn.value = false;
});

watch(
  () => [isMeetingMicOn.value, isSoundOn.value],
  ([micOn, soundOn]) => {
    if (micOn && soundOn) {
      toggleSound();
    }
  }
);

watch(
  () => languageSelected.value,
  (newVal) => {
    if (isSoundOn.value && newVal && !languageOptions.value[newVal.code].voiceOutputEnabled) {
      isSoundOn.value = false;
      window.dispatchEvent(new Event('unlock-audio'));

      toastStore.error(t('not_support_audio_play'));
    }
  }
);
</script>

<style scoped lang="scss"></style>
